import React, { useEffect, useState } from "react";
import { Button, Divider, Group, Modal, Select, Stack, Text } from "@mantine/core";
import { ROOM_NUMBERING_METHODS, type IRoomNumberingMethodKey } from "@multiconsult-group/multiplacer-core";
import { useAppDispatch } from "../../../app/hooks";
import { ModuleToolBarShell } from "../ModuleToolBarShell";
import { thunkCalculateRoomNumbers } from "../../../RoomNumbering/roomNumberingThunks";
import { setIfcClassVisibilityInRedux } from "../../designSlice";
import { RoomNumberingExportModal } from "../../../RoomNumbering/RoomNumberingExportModal";
import { SelectedRoomsRoomNumberingActionsMenu } from "../../../RoomNumbering/SelectedRoomsRoomNumberingActionsMenu";

const roomNumberingMethodOptions = ROOM_NUMBERING_METHODS.map((method) => ({
  value: method.key,
  label: method.title,
  description: method.summary,
}));

export const RoomNumberingBar: React.FC = () => {
  const dispatch = useAppDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedMethodKey, setSelectedMethodKey] = useState<IRoomNumberingMethodKey>(
    ROOM_NUMBERING_METHODS[0].key
  );
  const selectedMethod =
    ROOM_NUMBERING_METHODS.find((method) => method.key === selectedMethodKey) ??
    ROOM_NUMBERING_METHODS[0];

  useEffect(() => {
    dispatch(setIfcClassVisibilityInRedux({ ifcClass: "IfcDoor", visible: true }));
  }, [dispatch]);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await dispatch(thunkCalculateRoomNumbers(selectedMethodKey));
      setModalOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ModuleToolBarShell>
        <Group align="center" gap="md" wrap="nowrap">
          <Group gap="sm" align="center" wrap="nowrap">
            <Text fw={600} size="sm">
              Romnummerering
            </Text>
            <Divider orientation="vertical" h={24} />
            <Text size="sm" c="dimmed">
              Beregn romnummer for aktiv etasje
            </Text>
          </Group>

          <Button size="sm" onClick={() => setModalOpen(true)}>
            Beregn romnummer
          </Button>
          <Button size="sm" variant="light" onClick={() => setExportModalOpen(true)}>
            Eksporter
          </Button>
          <Divider orientation="vertical" h={24} />
          <SelectedRoomsRoomNumberingActionsMenu />
        </Group>
      </ModuleToolBarShell>

      <RoomNumberingExportModal
        opened={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
      />

      <Modal
        opened={modalOpen}
        onClose={() => {
          if (!isSubmitting) {
            setModalOpen(false);
          }
        }}
        title="Beregn romnummer"
        centered
      >
        <Stack gap="lg">
          <Text size="sm">Vil du beregne romnummer på nåværende etasje?</Text>
          <Select
            label="Romnummerering"
            data={roomNumberingMethodOptions}
            value={selectedMethodKey}
            onChange={(value) => {
              if (value != null) {
                setSelectedMethodKey(value);
              }
            }}
            allowDeselect={false}
            disabled={isSubmitting}
          />
          <Stack gap={4}>
            <Text size="sm" fw={600}>
              {selectedMethod.title}
            </Text>
            <Text size="sm" c="dimmed">
              Kunde: {selectedMethod.customer}
            </Text>
            <Text size="sm" c="dimmed">
              PDF: {selectedMethod.pdfFileName}
            </Text>
            {selectedMethod.summary != null && (
              <Text size="sm" c="dimmed">
                {selectedMethod.summary}
              </Text>
            )}
          </Stack>

          <Group justify="flex-end">
            <Button variant="outline" onClick={() => setModalOpen(false)} disabled={isSubmitting}>
              Avbryt
            </Button>
            <Button onClick={() => void handleConfirm()} loading={isSubmitting}>
              Beregn romnummer
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
